import React from "react";
import "./RemindersCard.css";
import { Heart, Trash2, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { toast } from "react-toastify";

const RemindersCard = ({ reminders = [], setReminders }) => {
  const navigate = useNavigate();

  const getLoggedInName = () => {
    const user =
      JSON.parse(localStorage.getItem("kuddleUser") || "{}") ||
      JSON.parse(sessionStorage.getItem("kuddleUser") || "{}");

    return (
      user.name ||
      user.fullName ||
      user.firstName ||
      user.displayName ||
      user.email?.split("@")[0] ||
      "Me"
    );
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await api.put(`/api/reminders/${id}`, { status });

      setReminders((prev) =>
        prev.map((r) =>
          r._id === id ? { ...r, status } : r
        )
      );
    } catch (err) {
      console.error("❌ Failed to update status:", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/reminders/${id}`);
      setReminders((prev) => prev.filter((reminder) => reminder._id !== id));
      window.dispatchEvent(new Event("kuddle:reminders-updated"));
    } catch (err) {
      console.error("❌ Failed to delete reminder:", err);
      toast.error(err.message || "Delete failed");
    }
  };

  const getReminderAudienceLabel = (reminder) => {
    if (!reminder) return "";

    if (reminder.targetName) {
      return reminder.targetName === "Myself"
        ? getLoggedInName()
        : reminder.targetName;
    }

    if (reminder.audience === "role" && reminder.target === "staff") {
      return "All Staff";
    }

    if (reminder.audience === "all") {
      return reminder.target === "classrooms" ? "All Classrooms" : "Everyone";
    }

    if (reminder.audience === "individual") {
      return getLoggedInName();
    }

    if (reminder.audience === "classroom") {
      return "Classroom";
    }

    if (reminder.audience === "parent") {
      return "Parents";
    }

    if (reminder.audience === "student") {
      return "Students";
    }

    return reminder.target || reminder.audience || "";
  };

  const handleComplete = async (id) => {
    try {
      const res = await api.patch(`/api/reminders/${id}/complete`);

      setReminders((prev) =>
        prev.map((r) => (r._id === id ? res.data : r))
      );

      window.dispatchEvent(new Event("kuddle:reminders-updated"));

    } catch (err) {
      console.error("Complete reminder failed:", err);
      toast.error("Failed to complete reminder");
    }
  };

  return (
    <div className="dashboard-card reminders-card">
      <div className="reminders-card-header">
        <h3>
          <Heart size={18} className="icon" />
          Reminders
        </h3>

        <button
          type="button"
          className="reminders-link-btn"
          onClick={() => navigate("/reminders")}
        >
          View all
        </button>
      </div>

      {reminders.length > 0 ? (
        <ul className="reminders-list">
          {reminders.slice(0, 5).map((reminder) => (
            <li key={reminder._id} className="reminder-item">
              <div className="reminder-message">
                <span className={`status-badge ${reminder.status}`}>
                  {reminder.status?.replace("_", " ") || "pending"}
                </span>

                <div className="reminder-text">{reminder.message}</div>

                <div className="reminder-meta">
                  {getReminderAudienceLabel(reminder)}
                </div>

                {(reminder.remindAt || reminder.createdAt || reminder.date) && (
                  <small className="reminder-date">
                    {new Date(
                      reminder.remindAt || reminder.createdAt || reminder.date
                    ).toLocaleString(undefined, {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </small>
                )}
              </div>

              {reminder.status !== "completed" && (
                <button
                  type="button"
                  className="reminder-done-btn"
                  onClick={() => handleComplete(reminder._id)}
                  title="Mark completed"
                >
                  <CheckCircle2 size={16} />
                </button>
              )}

              <button
                type="button"
                className="delete-btn"
                onClick={() => handleDelete(reminder._id)}
                title="Delete reminder"
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="reminders-empty">No reminders yet.</div>
      )}
    </div>
  );
};

export default RemindersCard;